import { type FC, type ReactNode } from "react";

import { Modal, Skeleton } from "@snapchat/snap-design-system";

import snapchatSvg from "@app-assets/svg/snapchat.svg";

import { type TosMetadata } from "../../../gql-generated/types";

import styles from "./TosModal.module.css";
import { TosModalFooter } from "./components/TosModalFooter";
import { TosModalHeader } from "./components/TosModalHeader";
import { SCROLL_COMPLETION_THRESHOLD } from "./constants";
import { useTosModalState } from "./hooks/useTosModalState";
import { useTosScrollDetection } from "./hooks/useTosScrollDetection";
import { getTosHeaderText, getTosStepData } from "./utils";

interface TosModalProps {
    tosHeading: ReactNode;
    tosMetadata?: TosMetadata;
    visible: boolean;
    disabled: boolean;
    loading: boolean;
    accepted: boolean;
    onAccept: () => Promise<void>;
    onDismiss: () => void;
    initialStepKey?: string;
}

export const TosModal: FC<TosModalProps> = ({
    visible,
    disabled,
    loading,
    accepted,
    onAccept,
    onDismiss,
    tosMetadata,
    initialStepKey,
}) => {
    const { terms } = tosMetadata ?? {};

    const {
        currentStep,
        completedSteps,
        isMultiStep,
        totalSteps,
        isLastStep,
        isCurrentStepCompleted,
        handleStepNavigation,
        handleNextStep,
        markStepCompleted,
    } = useTosModalState({
        terms,
        visible,
        tosMetadata,
        accepted,
        onAccept,
        initialStepKey,
    });

    const { contentRef } = useTosScrollDetection({
        currentStep,
        completedSteps,
        threshold: SCROLL_COMPLETION_THRESHOLD,
        onStepCompleted: markStepCompleted,
    });

    const currentStepData = terms ? getTosStepData(terms, currentStep) : null;
    const headerText = getTosHeaderText(currentStepData?.key);

    return (
        <Modal
            visible={visible}
            maskClosable={!loading}
            onCancel={onDismiss}
            footer={
                <TosModalFooter
                    isMultiStep={isMultiStep}
                    totalSteps={totalSteps}
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    accepted={accepted}
                    loading={loading}
                    disabled={disabled}
                    isLastStep={isLastStep}
                    isCurrentStepCompleted={isCurrentStepCompleted}
                    onStepClick={handleStepNavigation}
                    onNext={handleNextStep}
                    onDismiss={onDismiss}
                />
            }
            title={
                <TosModalHeader
                    iconSrc={snapchatSvg}
                    headerText={headerText}
                    sourceUrl={currentStepData?.sourceUrl}
                    effectiveDate={currentStepData?.effectiveDate}
                    tosMetadata={tosMetadata}
                />
            }
        >
            {tosMetadata ? (
                <div ref={contentRef} className={styles.tosContentWrapper}>
                    {currentStepData?.content}
                </div>
            ) : (
                <Skeleton height={250} />
            )}
        </Modal>
    );
};
